// Recovers from stale-chunk failures after a web deploy. When a dynamic import
// resolves to the SPA catchall HTML (served at 200) or fails to load, reload the
// page once so the browser fetches the fresh index.html and current chunk hashes.
// Web-only: relies on `sessionStorage` / `window.location`, so keep this out of
// any shared/native module graph.

const RELOAD_KEY = "chunkReloadAt";
const COOLDOWN_MS = 30_000;

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { name, message } = error as { name?: string; message?: string };
  if (name === "ChunkLoadError") return true;
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(
    message ?? "",
  );
};

// Reloads at most once per cooldown window. A failure that recurs within the
// window falls through (returns false) so the caller can surface a real error UI
// instead of looping; a later deploy on a long-lived tab still gets one reload.
export const reloadOnceForStaleChunk = (): boolean => {
  // sessionStorage can throw (private mode, storage disabled, sandboxed frames).
  // This runs inside an error handler, so if we can't track reload state we must
  // not throw — bail and let the error boundary show instead of risking a loop.
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};
